import React, { useState, useEffect } from 'react';
import { Card, Typography, Button, Space, Table, Input, message, Alert } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, OrderedListOutlined } from '@ant-design/icons';
import { animalService, categoriaService } from '../../../services/api';

const { Title, Text } = Typography;

// Tab para ordenar y ALIASAR cómo aparecen las categorías en el micrositio.
//   - Orden: config.orden_categorias (array de id_categoria).
//   - Alias: config.alias_categorias ({ [id_categoria]: 'texto mostrado' }).
// El productor NO edita la categoría CANÓNICA (esa es global, compartida y
// afecta el histórico); solo elige el orden y un alias de MARCA por evento.
// Alias vacío → se muestra la canónica. Aplica solo a este evento.
const OrdenCategoriasTab = ({ eventoId, config, onChange }) => {
  const [cats, setCats] = useState([]);   // [{ id_categoria, nom_categoria }]
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (eventoId) cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoId]);

  const cargar = async () => {
    setLoading(true);
    try {
      const [resEvento, resCat] = await Promise.all([
        animalService.getByEvento(eventoId),
        categoriaService.getAll(),
      ]);
      const animales = resEvento.data?.animales || resEvento.data || [];
      const catMap = Object.fromEntries(
        (resCat.data?.categorias || resCat.data || []).map(c => [c.id_categoria, c.nom_categoria])
      );
      // Solo categorías que tienen al menos un animal en este evento.
      const idsUsadas = [...new Set(animales.map(a => a.id_categoria).filter(Boolean))];
      const orden = config.orden_categorias || [];
      const ordenadas = [...idsUsadas].sort((a, b) => {
        const ia = orden.indexOf(a); const ib = orden.indexOf(b);
        return (ia === -1 ? Infinity : ia) - (ib === -1 ? Infinity : ib);
      });
      setCats(ordenadas.map(id => ({ id_categoria: id, nom_categoria: catMap[id] || `Cat. ${id}` })));
    } catch (err) {
      message.error(err?.response?.data?.error || 'Error al cargar las categorías del evento');
    } finally {
      setLoading(false);
    }
  };

  const mover = (idx, dir) => {
    const destino = idx + dir;
    if (destino < 0 || destino >= cats.length) return;
    const next = [...cats];
    [next[idx], next[destino]] = [next[destino], next[idx]];
    setCats(next);
    onChange('orden_categorias', next.map(c => c.id_categoria));
  };

  const setAlias = (id, val) => {
    const next = { ...(config.alias_categorias || {}) };
    const v = val.trim();
    if (v) next[id] = v; else delete next[id];   // vacío → se borra (usa canónica)
    onChange('alias_categorias', next);
  };

  const aliasDe = (id) => (config.alias_categorias || {})[id] || '';

  const columns = [
    {
      title: '#', dataIndex: 'orden', width: 48, align: 'center',
      render: (_, __, idx) => <Text type="secondary">{idx + 1}</Text>,
    },
    {
      title: 'Categoría (canónica)', dataIndex: 'nom_categoria',
      render: (v) => <Text>{v}</Text>,   // solo lectura
    },
    {
      title: 'Alias (cómo se ve en el sitio)', dataIndex: 'alias', width: 320,
      render: (_, r) => (
        <Input
          size="small"
          placeholder={r.nom_categoria}
          value={aliasDe(r.id_categoria)}
          onChange={(e) => setAlias(r.id_categoria, e.target.value)}
        />
      ),
    },
    {
      title: 'Orden', key: 'orden_acc', width: 90, align: 'center',
      render: (_, __, idx) => (
        <Space size={2}>
          <Button size="small" icon={<ArrowUpOutlined />} disabled={idx === 0} onClick={() => mover(idx, -1)} />
          <Button size="small" icon={<ArrowDownOutlined />} disabled={idx === cats.length - 1} onClick={() => mover(idx, 1)} />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '16px 0' }}>
      <Title level={4}><OrderedListOutlined /> Orden y alias de Categorías</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        Ordená cómo aparecen las categorías en el filtro y el menú del micrositio, y opcionalmente ponéles un
        <strong> alias</strong> para mostrar un nombre distinto SOLO en este remate (ej: "Vaquillas Premium" → "Vaquillas Elite").
      </Text>
      <Alert
        type="info" showIcon closable style={{ marginBottom: 12 }}
        message="El alias no cambia la categoría real. La canónica queda intacta (histórico y panel); el alias es solo la etiqueta que ve el comprador en este evento. Dejalo vacío para usar el nombre canónico."
      />
      <Card styles={{ body: { padding: 8 } }}>
        <Table
          scroll={{ x: 'max-content' }}
          loading={loading}
          dataSource={cats}
          columns={columns}
          rowKey="id_categoria"
          size="small"
          pagination={false}
          locale={{ emptyText: 'Este evento no tiene animales con categorías cargadas.' }}
        />
      </Card>
    </div>
  );
};

export default OrdenCategoriasTab;
